/**
 * The Add node window: name, kind, categories and the short fields of its environment (a
 * language) of a new node. It floats beside a
 * right click on the graph ("Add a node…"), and the node goes where the click was; key N opens
 * it for a node beside the selection; a link dropped on empty space opens it for a node linked
 * from where the link started. It offers the current environment's kinds only. Adding closes it
 * (the service `state/nodeAdder.ts` does the rest); Escape closes it too. Kinds of nodes come from plugins: in a project with none on
 * it says so and leads to the Plugins window.
 */
import { For, Show, createEffect, createSignal, on, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { CategoryPicker } from "../../shared/CategoryPicker";
import { FieldInputs } from "../../shared/FieldInputs";

/** The window. */
export function AddNodeWindow(): JSX.Element {
  const { nodeAdder, graph, fields, kinds, wm } = useApp();
  /** The kind picked: a built-in kind's id or a custom kind's. */
  const [kind, setKind] = createSignal("class");
  /** The values of the environment's fields given so far (they stay for the next node, like the kind). */
  const [data, setData] = createSignal<{ [key in string]: string }>({});
  const [categories, setCategories] = createSignal<string[]>([]);
  let name!: HTMLInputElement;

  // Each time the window is asked for, the name field is ready to type in (with the name asked for, if any).
  createEffect(
    on(nodeAdder.asked, () =>
      queueMicrotask(() => {
        if (!name) return;
        if (nodeAdder.name()) name.value = nodeAdder.name();
        name.focus();
      }),
    ),
  );
  // Another environment, other kinds: the pick follows.
  createEffect(() => {
    const offered = kinds.nodes();
    if (offered.length && !offered.some((k) => k.id === kind())) setKind(offered[0]!.id);
  });
  /** The name of the node the new one will be linked from. */
  const source = () => {
    const id = nodeAdder.from();
    return id ? (graph.state.graph.nodes.find((n) => n.id === id)?.name ?? id) : null;
  };

  /** Sends the node; on success the fields are emptied for the next one (kind and categories stay). */
  const submit = async (e: SubmitEvent) => {
    e.preventDefault();
    const picked =
      kinds.nodes().find((k) => k.id === kind()) ?? kinds.nodes()[0];
    if (!picked) return;
    const added = await nodeAdder.add({
      name: name.value,
      kind: picked.id,
      categories: categories(),
      data: data(),
    });
    if (added) name.value = "";
  };

  return (
    <Show
      when={kinds.nodes().length}
      fallback={
        <div class="nform addnode" id="add-node" data-no-kinds>
          <p class="small">
            This project has no kinds of nodes yet: they come with plugins. Turn
            one on for this project, such as Coding.
          </p>
          <div class="btnrow">
            <button
              class="btn primary"
              type="button"
              id="add-node-plugins"
              onClick={() => wm.open("plugins")}
            >
              Choose plugins
            </button>
            <button class="btn" type="button" onClick={() => nodeAdder.close()}>
              Cancel
            </button>
          </div>
        </div>
      }
    >
      <form
        class="nform addnode"
        id="add-node"
        data-linked-from={nodeAdder.from() ?? undefined}
        onSubmit={(e) => void submit(e)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            nodeAdder.close();
          }
        }}
      >
        <p class="muted small" id="add-node-where">
          {source()
            ? `The node goes where you dropped the link, linked from ${source()}.`
            : nodeAdder.at()
              ? "The node goes where you clicked."
              : "The node goes beside the selected one, or where the layout finds room."}
        </p>
        <Show when={nodeAdder.attachments().length}>
          <p class="muted small" id="add-node-files">
            Attached to {nodeAdder.attachments().join(", ")}.
          </p>
        </Show>
        <label class="fl">
          Name
          <input
            class="field"
            id="new-name"
            ref={name}
            placeholder="For example OrderService"
            autocomplete="off"
          />
        </label>
        <label class="fl">
          Kind
          <select
            class="select"
            id="new-kind"
            value={kind()}
            onChange={(e) => setKind(e.currentTarget.value)}
          >
            <For each={kinds.nodes()}>
              {(k) => (
                <option value={k.id} selected={k.id === kind()}>
                  {k.label}
                </option>
              )}
            </For>
          </select>
        </label>
        <FieldInputs idPrefix="new" fields={fields.list()} data={data()} choices={fields.choices} onAdd={fields.add} onChange={setData} short />
        <div class="fl">
          Categories
          <CategoryPicker
            id="new-categories"
            value={categories()}
            known={graph.categories()}
            onChange={setCategories}
          />
        </div>
        <div class="btnrow">
          <button class="btn primary" type="submit" id="add-node-submit">
            Add node
          </button>
          <button class="btn" type="button" onClick={() => nodeAdder.close()}>
            Cancel
          </button>
        </div>
      </form>
    </Show>
  );
}
